import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../Shared/services/api';
import { getSlotPackages } from '../../BookingManagement/services/service';
import { EndpointQueryKeys } from '../../BookingManagement/constants/endpoints';
import { updateCpm } from '../services/service';
import {
  GetSlotPackagesResponse,
  UpdateCpmPayload,
  UpdateCpmResponse,
} from '../interfaces/service.interfaces';
import { useAppContext } from '../../Shared/store/appContext';
import { ApiError } from '../../Shared/interfaces/ApiError';

const NUMERIC_ONLY_REGEX = /^\d+$/;
const IMPRESSIONS_BLOCKED_ERROR_MARKER =
  'Total impressions cannot be changed while bookings exist';

const resolveErrorMessageKey = (error: ApiError): string =>
  error?.message?.includes(IMPRESSIONS_BLOCKED_ERROR_MARKER)
    ? 'edit-inventory-impressions-blocked-error-message'
    : 'edit-inventory-error-message';

export const useEditInventoryForm = () => {
  const api = useApiClient();
  const { state } = useAppContext();

  // State
  const [selectedArtEvent, setSelectedArtEvent] = useState<string>('');
  const [selectedTypeOfPurchase, setSelectedTypeOfPurchase] =
    useState<string>('');
  const [selectedSlotPackage, setSelectedSlotPackage] = useState<string>('');
  const [selectedSegment, setSelectedSegment] = useState<string>('');
  const [newCpm, setNewCpm] = useState<string>('');
  const [newTotalImpressions, setNewTotalImpressions] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [validationData, setValidationData] =
    useState<UpdateCpmResponse | null>(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] =
    useState<boolean>(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState<boolean>(false);
  const [errorMessageKey, setErrorMessageKey] = useState<string>(
    'edit-inventory-error-message'
  );

  // Queries
  const { data: slotPackagesResponse, isLoading: isLoadingSlotPackages } =
    useQuery<GetSlotPackagesResponse, ApiError>({
      queryKey: [
        EndpointQueryKeys.GET_SLOT_PACKAGES,
        selectedTypeOfPurchase,
        selectedArtEvent,
      ],
      queryFn: () =>
        getSlotPackages(api, selectedTypeOfPurchase, selectedArtEvent),
      enabled: !!selectedTypeOfPurchase && !!selectedArtEvent,
    });

  const updateCpmMutation = useMutation<
    UpdateCpmResponse,
    ApiError,
    UpdateCpmPayload
  >({
    mutationFn: (payload: UpdateCpmPayload) => updateCpm(api, payload),
  });

  // Effects
  useEffect(() => {
    if (state.selectedArtEventId) {
      setSelectedArtEvent(state.selectedArtEventId);
    }
  }, [state.selectedArtEventId]);

  useEffect(() => {
    setSelectedTypeOfPurchase('');
    setSelectedSlotPackage('');
    setSelectedSegment('');
    setNewCpm('');
    setNewTotalImpressions('');
  }, [selectedArtEvent]);

  useEffect(() => {
    setSelectedSlotPackage('');
    setSelectedSegment('');
    setNewCpm('');
    setNewTotalImpressions('');
  }, [selectedTypeOfPurchase]);

  useEffect(() => {
    setSelectedSegment('');
    setNewCpm('');
    setNewTotalImpressions('');
  }, [selectedSlotPackage]);

  useEffect(() => {
    setNewCpm('');
    setNewTotalImpressions('');
  }, [selectedSegment]);

  // Derived state
  const slotPackages = useMemo(
    () => slotPackagesResponse?.slotPackages || [],
    [slotPackagesResponse]
  );

  const selectedPackage = useMemo(
    () =>
      slotPackages.find((pkg) => pkg.slotPackageName === selectedSlotPackage),
    [slotPackages, selectedSlotPackage]
  );

  const segments = useMemo(
    () => selectedPackage?.segments || [],
    [selectedPackage]
  );

  const currentSegment = useMemo(
    () => segments.find((seg) => seg.segmentName === selectedSegment),
    [segments, selectedSegment]
  );

  const oldCpm = currentSegment?.cpm ?? 0;
  const oldTotalImpressions = currentSegment?.overAllImpressions ?? 0;

  // Handlers
  const resetForm = () => {
    setSelectedTypeOfPurchase('');
    setSelectedSlotPackage('');
    setSelectedSegment('');
    setNewCpm('');
    setNewTotalImpressions('');
  };

  const handleNewCpmChange = (value: string) => {
    if (value === '' || NUMERIC_ONLY_REGEX.test(value)) {
      setNewCpm(value);
    }
  };

  const handleNewTotalImpressionsChange = (value: string) => {
    if (value === '' || NUMERIC_ONLY_REGEX.test(value)) {
      setNewTotalImpressions(value);
    }
  };

  const buildPayload = (validateOnly: boolean): UpdateCpmPayload => ({
    artEventId: selectedArtEvent,
    slotPackageName: selectedSlotPackage,
    segmentName: selectedSegment,
    typeOfPurchase: selectedTypeOfPurchase,
    oldCpm,
    oldTotalImpressions,
    // A blank "new" field is sent as the current value (unchanged).
    newCpm: newCpm ? Number(newCpm) : oldCpm,
    newTotalImpressions: newTotalImpressions
      ? Number(newTotalImpressions)
      : oldTotalImpressions,
    validateOnly,
  });

  const handleSubmit = () => {
    updateCpmMutation.mutate(buildPayload(true), {
      onSuccess: (data) => {
        setValidationData(data);
        setIsModalOpen(true);
      },
      onError: (error) => {
        setErrorMessageKey(resolveErrorMessageKey(error));
        setShowErrorSnackbar(true);
      },
    });
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setValidationData(null);
    resetForm();
  };

  const handleModalConfirm = () => {
    updateCpmMutation.mutate(buildPayload(false), {
      onSuccess: () => {
        setIsModalOpen(false);
        setValidationData(null);
        resetForm();
        setShowSuccessSnackbar(true);
      },
      onError: (error) => {
        setIsModalOpen(false);
        setValidationData(null);
        setErrorMessageKey(resolveErrorMessageKey(error));
        setShowErrorSnackbar(true);
      },
    });
  };

  return {
    formFieldProps: {
      selectedArtEvent,
      selectedTypeOfPurchase,
      setSelectedTypeOfPurchase,
      selectedSlotPackage,
      setSelectedSlotPackage,
      selectedSegment,
      setSelectedSegment,
      newCpm,
      handleNewCpmChange,
      oldCpm,
      newTotalImpressions,
      handleNewTotalImpressionsChange,
      oldTotalImpressions,
      slotPackages,
      segments,
      isLoadingSlotPackages,
    },
    submitProps: {
      handleSubmit,
      isSubmitting: updateCpmMutation.isPending,
    },
    modalProps: {
      isModalOpen,
      validationData,
      handleModalCancel,
      handleModalConfirm,
    },
    snackbarProps: {
      showSuccessSnackbar,
      setShowSuccessSnackbar,
      showErrorSnackbar,
      setShowErrorSnackbar,
      errorMessageKey,
    },
  };
};
